/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, Fragment, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate } from "react-admin";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Chip from "@material-ui/core/Chip";
import { Controller, useForm } from "react-hook-form";
import {
  Grid,
  Divider,
  Typography,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { TextFieldInForm } from "components/Form";
import ActionMenu from "components/ActionMenu";
import ButtonFooter from "components/ButtonFooter";
import {
  getDepartmentCategoryDetail,
  createDepartmentCategoryDetail,
  updateDepartmentCategoryDetail,
  changeDepartmentCategoryStatus,
  getDepartmentCategoryAvailableList,
} from "services/setting";
import Model from "models/departmentCategory";
import SnackbarUtils from "utils/SnackbarUtils";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 4,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  embeddedPanel: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    backgroundColor: "#fff",
  },
  embeddedHeader: {
    padding: theme.spacing(3, 3, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  embeddedHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  embeddedHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  embeddedTitle: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  codeChip: {
    borderRadius: 10,
    fontWeight: 700,
    backgroundColor: "#f3f5fb",
    color: "#546179",
  },
  embeddedSubtitle: {
    color: "#f08a24",
    fontSize: "0.83rem",
    marginTop: theme.spacing(0.75),
  },
  embeddedContent: {
    padding: theme.spacing(1),
    overflow: "auto",
    flex: 1,
  },
  sectionTitle: {
    fontWeight: 700,
    color: "#1f2a44",
    marginBottom: theme.spacing(1),
  },
  statusLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#546179",
    marginRight: theme.spacing(0.5),
  },
  listBoxContainer: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  listBox: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    minHeight: 250,
    maxHeight: 350,
    overflow: "auto",
    padding: theme.spacing(0.5),
    backgroundColor: "#fbfcff",
  },
  listBoxHeader: {
    padding: theme.spacing(1, 1.25),
    backgroundColor: "#f4f6fb",
    fontWeight: "bold",
    fontSize: "0.8rem",
    position: "sticky",
    top: 0,
    zIndex: 1,
    borderRadius: 8,
    textTransform: "uppercase",
    color: "#39445d",
  },
  listBoxItem: {
    padding: theme.spacing(1, 1.25),
    cursor: "pointer",
    borderRadius: 8,
    marginTop: theme.spacing(0.5),
    border: "1px solid #e3e8f4",
    backgroundColor: "#fff",
    width: "100%",
    textAlign: "left",
    appearance: "none",
    font: "inherit",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.selected": {
      backgroundColor: "#e8ecff",
      color: "#243665",
      fontWeight: 600,
      borderColor: "#b9c4ff",
    },
  },
  memberItem: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  memberCode: {
    color: "#4256d0",
    fontWeight: 700,
    fontSize: "0.82rem",
  },
  memberDesc: {
    color: "#1f2a44",
    fontSize: "0.9rem",
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#ffffff",
    color: "#546179",
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  moveButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    paddingInline: theme.spacing(0.5),
  },
  moveButton: {
    minWidth: 44,
    width: 44,
    height: 44,
    borderRadius: 22,
    boxShadow: "none",
  },
  removeButton: {
    minWidth: 28,
    width: 28,
    height: 28,
    color: "#ff6b81",
  },
  footerActions: {
    paddingTop: theme.spacing(2),
  },
}));

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle disableTypography {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const isSameDepartment = (d1, d2) => {
  const id1 = d1?.Id ?? d1?.id;
  const id2 = d2?.Id ?? d2?.id;
  if (id1 && id2 && Number(id1) === Number(id2)) {
    return true;
  }
  const code1 = d1?.DeptCode ?? d1?.Deptcode ?? d1?.deptCode;
  const code2 = d2?.DeptCode ?? d2?.Deptcode ?? d2?.deptCode;
  if (code1 && code2 && String(code1).trim().toLowerCase() === String(code2).trim().toLowerCase()) {
    return true;
  }
  return false;
};

const sortDepartments = (list) => {
  return [...list].sort((a, b) => {
    const codeA = String(a.DeptCode || a.Deptcode || a.deptCode || "").trim().toLowerCase();
    const codeB = String(b.DeptCode || b.Deptcode || b.deptCode || "").trim().toLowerCase();
    return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: "base" });
  });
};

const DialogItem = (props) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { id, mode, setMode, open, onClose, embedded = false, availableDepts: propsAvailableDepts } = props;
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [data, setData] = useState(Model);
  const [availableDepts, setAvailableDepts] = useState([]);
  const [assignedDepts, setAssignedDepts] = useState([]);
  const mountedRef = useRef(true);
  const methods = useForm({ defaultValues: data });

  const { handleSubmit, reset, watch, setValue } = methods;
  const isActive = watch("Active");
  const watchedDeptCateCode = watch("DeptCateCode");
  const watchedDescription = watch("Description");
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadData = useCallback(async () => {
    setBtnLoading(true);
    try {
      if (id && id !== 0) {
        const [availableResponse, response] = await Promise.all([
          propsAvailableDepts ? Promise.resolve(null) : getDepartmentCategoryAvailableList(),
          getDepartmentCategoryDetail(id),
        ]);
        if (mountedRef.current) {
          const rawAvailable = propsAvailableDepts || availableResponse?.Data || [];
          const assigned = response?.Departments || [];
          const nextAvailable = rawAvailable.filter(
            (dept) =>
              !assigned.some((item) => isSameDepartment(item, dept))
          );

          setData(response);
          reset(response);
          setAssignedDepts(assigned);
          setAvailableDepts(sortDepartments(nextAvailable));
        }
      } else {
        const availableResponse = propsAvailableDepts ? null : await getDepartmentCategoryAvailableList();
        if (mountedRef.current) {
          const rawAvailable = propsAvailableDepts || availableResponse?.Data || [];
          setData(Model);
          reset(Model);
          setAssignedDepts([]);
          setAvailableDepts(sortDepartments(rawAvailable));
        }
      }
    } finally {
      if (mountedRef.current) {
        setBtnLoading(false);
      }
    }
  }, [id, reset, propsAvailableDepts]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, id]);

  const handleClose = (value) => {
    onClose(value);
  };

  const saveDepartmentCategory = async (values) => {
    if (mode === "edit") {
      const payload = { ...values, Id: id };
      const { Code, InternalMessage, UserMessage, MoreInfo } =
        await updateDepartmentCategoryDetail(payload);
      if (Code === 0) {
        SnackbarUtils.success(UserMessage);
        handleClose(id);
      } else if (MoreInfo === "DEPARTMENT_CATEGORY_HAS_MEMBERS") {
        SnackbarUtils.warning(
          translate("ra.field.Remove Department Members Before Deactivate"),
        );
      } else if (InternalMessage) {
        SnackbarUtils.error(InternalMessage);
      } else {
        SnackbarUtils.warning(UserMessage);
      }
      if (mountedRef.current) {
        setBtnLoading(false);
      }
      return;
    }

    const { Code, InternalMessage, UserMessage } =
      await createDepartmentCategoryDetail(values);
    if (Code === 0) {
      SnackbarUtils.success(UserMessage);
      handleClose(parseInt(InternalMessage, 10));
    } else if (InternalMessage) {
      SnackbarUtils.error(InternalMessage);
    } else {
      SnackbarUtils.warning(UserMessage);
    }
    if (mountedRef.current) {
      setBtnLoading(false);
    }
  };

  const onSubmit = (values) => {
    if (values.Active === false && assignedDepts.length > 0) {
      SnackbarUtils.warning(
        translate("ra.field.Remove Department Members Before Deactivate"),
      );
      return;
    }

    setBtnLoading(true);
    const nextValues = {
      ...data,
      ...values,
      DeptCode: assignedDepts.map((d) => ({
        DeptCode: d.DeptCode || d.Deptcode || d.deptCode,
        Description: d.Description || d.description || "",
      })),
      UserModified: Model.UserModified,
    };
    setData(nextValues);
    saveDepartmentCategory(nextValues);
  };

  const confirmStatusChange = (nextActive, itemId = id) => {
    if (!nextActive && assignedDepts.length > 0) {
      SnackbarUtils.warning(
        translate("ra.field.Remove Department Members Before Deactivate"),
      );
      return;
    }

    SnackbarUtils.delConfirmWithTitle(
      translate(
        nextActive ? "ra.actionMenu.Activate" : "ra.actionMenu.Deactivate",
      ),
      translate(
        nextActive
          ? "ra.question.confirmActivateDepartmentCategory"
          : "ra.question.confirmDeactivateDepartmentCategory",
      ),
      async function () {
        setBtnLoading(true);
        try {
          const { Code, UserMessage, InternalMessage, MoreInfo } =
            await changeDepartmentCategoryStatus(itemId, nextActive);
          if (Code === 0) {
            setValue("Active", nextActive);
            setData((current) => ({ ...current, Active: nextActive }));
            SnackbarUtils.success(UserMessage);
            handleClose(itemId);
          } else if (MoreInfo === "DEPARTMENT_CATEGORY_HAS_MEMBERS") {
            SnackbarUtils.warning(
              translate("ra.field.Remove Department Members Before Deactivate"),
            );
          } else if (InternalMessage) {
            SnackbarUtils.error(InternalMessage);
          } else {
            SnackbarUtils.warning(UserMessage);
          }
        } finally {
          if (mountedRef.current) {
            setBtnLoading(false);
          }
        }
      },
    );
  };

  const handleActiveChange = (event) => {
    const nextActive = event.target.checked;
    if (mode === "view") {
      confirmStatusChange(nextActive);
      return;
    }
    if (!nextActive && assignedDepts.length > 0) {
      SnackbarUtils.warning(
        translate("ra.field.Remove Department Members Before Deactivate"),
      );
      return;
    }
    setValue("Active", nextActive);
  };

  const handleCancel = () => {
    if (id === 0) {
      onClose();
      return;
    }
    loadData();
    setMode("view");
  };

  const handleMoveToAssigned = (dept) => {
    if (mode === "view") return;
    setAvailableDepts((prev) => prev.filter((item) => !isSameDepartment(item, dept)));
    setAssignedDepts((prev) => [...prev, dept]);
  };

  const handleMoveToAvailable = (dept) => {
    if (mode === "view") return;
    setAssignedDepts((prev) => prev.filter((item) => !isSameDepartment(item, dept)));
    setAvailableDepts((prev) => sortDepartments([...prev, dept]));
  };

  const menuControlProp = [
    {
      name: "Edit",
      fnc: () => setMode("edit"),
      disabled: mode !== "view" || id === 0,
    },
  ];

  const panelTitle =
    mode === "add"
      ? translate("ra.field.Create New Group")
      : watchedDescription || props.title;

  const formContent = (
    <Fragment>
      {mode === "view" && id !== 0 ? (
        <ActionMenu menuControl={menuControlProp} justify="flex-end" size="auto" />
      ) : null}
      {mode === "view" && id !== 0 ? <Divider style={{ marginBottom: 16 }} /> : null}

      <form>
        <Grid container justifyContent="flex-start" spacing={2}>
          <Grid item xs={12} md={6}>
            <TextFieldInForm
              label={`* ${translate("ra.field.Category Code")}`}
              name="DeptCateCode"
              variant="outlined"
              margin="dense"
              methods={methods}
              disabled={mode !== "add" || id !== 0}
              rule={{
                required: {
                  value: true,
                  message: translate("ra.validation.required"),
                },
                maxLength: {
                  value: 20,
                  message: "maximum length is 20",
                },
              }}
            />
            {mode === "add" ? (
              <Typography className={classes.embeddedSubtitle}>
                {translate("ra.field.This code cannot be changed once saved.")}
              </Typography>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldInForm
              label={`* ${translate("ra.field.Description")}`}
              name="Description"
              variant="outlined"
              margin="dense"
              methods={methods}
              disabled={mode === "view"}
              rule={{
                required: {
                  value: true,
                  message: translate("ra.validation.required"),
                },
                maxLength: {
                  value: 255,
                  message: "maximum length is 255",
                },
              }}
            />
          </Grid>
          {!embedded && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="Active"
                    control={methods.control}
                    defaultValue={true}
                    render={({ value }) => (
                      <Switch
                        checked={Boolean(value)}
                        onChange={handleActiveChange}
                        color="primary"
                        disabled={isBtnLoading}
                      />
                    )}
                  />
                }
                label={translate("ra.field.Active")}
              />
            </Grid>
          )}
        </Grid>

        {id !== 0 && (
          <div style={{ marginTop: 24 }}>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="h6" className={classes.sectionTitle}>
              {translate("ra.field.Department Assignment")}
            </Typography>
            <div className={classes.listBoxContainer}>
              <div className={classes.listBox}>
                <div className={classes.listBoxHeader}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <span>{translate("ra.field.Available Departments")}</span>
                    <Chip
                      label={availableDepts.length}
                      size="small"
                      className={classes.countBadge}
                    />
                  </Grid>
                </div>
                {availableDepts.map((dept) => (
                  <button
                    type="button"
                    key={dept.Id}
                    className={classes.listBoxItem}
                    onClick={() => handleMoveToAssigned(dept)}
                    disabled={mode === "view"}
                  >
                    {dept.DeptCode} - {dept.Description}
                  </button>
                ))}
                {availableDepts.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                    {translate("ra.info.notransaction")}
                  </div>
                )}
              </div>

              <div className={classes.listBox}>
                <div className={classes.listBoxHeader}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <span>{translate("ra.field.Group Members")}</span>
                    <Chip
                      label={assignedDepts.length}
                      size="small"
                      className={classes.countBadge}
                    />
                  </Grid>
                </div>
                {assignedDepts.map((dept) => (
                  <button
                    type="button"
                    key={dept.Id}
                    className={classes.listBoxItem}
                    onClick={() => handleMoveToAvailable(dept)}
                    disabled={mode === "view"}
                  >
                    <div className={classes.memberItem}>
                      <div>
                        <div className={classes.memberCode}>{dept.DeptCode}</div>
                        <div className={classes.memberDesc}>{dept.Description}</div>
                      </div>
                    </div>
                  </button>
                ))}
                {assignedDepts.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                    {translate("ra.info.notransaction")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {process.env.NODE_ENV === "development" ? (
          <pre>{JSON.stringify(data, 0, 2)}</pre>
        ) : null}

        {mode !== "view" ? (
          <div className={classes.footerActions}>
            <ButtonFooter
              disabled={isBtnLoading}
              SaveFnc={handleSubmit(onSubmit)}
              CancelFnc={handleCancel}
              saveTitle={translate("ra.field.Save Group")}
            />
          </div>
        ) : null}
      </form>
    </Fragment>
  );

  if (embedded) {
    return (
      <div className={classes.embeddedPanel}>
        <div className={classes.embeddedHeader}>
          {id === 0 ? (
            <Typography variant="h5" className={classes.embeddedTitle}>
              {panelTitle}
            </Typography>
          ) : (
            <div className={classes.embeddedHeaderRow}>
              <div className={classes.embeddedHeaderLeft}>
                <Typography variant="h5" className={classes.embeddedTitle}>
                  {panelTitle}
                </Typography>
                <Chip
                  label={watchedDeptCateCode || "-"}
                  size="small"
                  className={classes.codeChip}
                />
              </div>
              <div>
                <span className={classes.statusLabel}>
                  {translate(
                    isActive ? "ra.field.Active" : "ra.field.In-Active",
                  ).toUpperCase()}
                </span>
                <Controller
                  name="Active"
                  control={methods.control}
                  defaultValue={true}
                  render={({ value }) => (
                    <Switch
                      checked={Boolean(value)}
                      onChange={handleActiveChange}
                      color="primary"
                      disabled={isBtnLoading}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>
        <div className={classes.embeddedContent}>{formContent}</div>
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose(event);
        }
      }}
      scroll="paper"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="scroll-dialog-title" onClose={onClose}>
        {props.title}
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default DialogItem;
